import { Formiz } from "@formiz/core";
import { isMaxLength, isMinLength, isNotEmptyString, isRequired } from "@formiz/validations";
import { Button, ButtonSize, Checkbox, IconVariants, Input, Modal, Size, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import { useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useState } from "react";

import { SiteLogo } from "@/components";
import { Col, FormField, Row } from "@/components/customComponents";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import useAuth from "@/services/useAuth";
import { isUsernameValid } from "@/utils";

import { NAME_MAX_LENGTH, NAME_MIN_LENGTH, USERNAME_MAX_LENGTH, USERNAME_MIN_LENGTH } from "../constants";
import { OnboardingStep, WelcomeAboardModalProps } from "../types";
import { getOnboardingDraft, updateOnboardingDraft } from "../utils";

function WelcomeAboardModal({ isUsernameAvailable, isCheckingUsername, userForm, setSelectedOnboardingStep }: WelcomeAboardModalProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { invalidateFields } = userForm;
  const isContinueVisuallyDisabled = !userForm.isValid || !isUsernameAvailable;
  const [stateIsMounted, setStateIsMounted] = useState(false);
  const [stateIsShowFieldErrors, setStateIsShowFieldErrors] = useState(false);

  const handleContinue = useCallback(() => {
    if (isContinueVisuallyDisabled) {
      setStateIsShowFieldErrors(true);
      return;
    }
    setSelectedOnboardingStep(OnboardingStep.COMMUNITY_GUIDELINES);
  }, [isContinueVisuallyDisabled, setSelectedOnboardingStep]);

  useEffect(() => {
    if (isUsernameAvailable === false) {
      invalidateFields({ username: t("user:username.error.unavailable") });
    }

    // used to re-render component to show invalidated username when user comes back from guidlines step
    if (!stateIsMounted) {
      setTimeout(() => {
        setStateIsMounted(true);
      }, 0);
    }
  }, [invalidateFields, isUsernameAvailable, stateIsMounted, t]);

  return (
    <Modal
      className="[&_.flex.items-center.gap-md]:w-full"
      hideCloseButton
      open={true}
      dismissable={false}
      backdrop="blur"
      title={
        <Col className="gap-lg">
          <Row className="justify-center">
            <SiteLogo type="icon" />
          </Row>
          <Text variant={TextVariants.MOBILE_HEADING_4_BOLD}>
            <Trans i18nKey="user:onboarding.welcomeAboard.modal.title" />
          </Text>
        </Col>
      }
      actions={
        <Button
          size={ButtonSize.LARGE}
          onClick={handleContinue}
          aria-disabled={isContinueVisuallyDisabled}
          className={isContinueVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
        >
          <Trans i18nKey="common:continue" />
        </Button>
      }
    >
      <Formiz
        connect={userForm}
        initialValues={getOnboardingDraft(user?.email) || {}}
        onChange={() => updateOnboardingDraft(user?.email, userForm.values)}
      >
        <form className="flex flex-col gap-lg">
          <FormField
            as="input"
            name="username"
            showError={stateIsShowFieldErrors || isUsernameAvailable === false}
            toLowerCase={true}
            validations={[
              {
                rule: isRequired(),
                message: t("user:username.error.required"),
              },
              {
                rule: isMinLength(USERNAME_MIN_LENGTH),
                message: t("user:username.error.tooShort"),
              },
              {
                rule: isMaxLength(USERNAME_MAX_LENGTH),
                message: t("user:username.error.tooLong", { maxLength: USERNAME_MAX_LENGTH }),
              },
              {
                rule: (value: string) => isUsernameValid(value),
                message: t("user:username.error.invalid"),
              },
            ]}
          >
            <Input
              required
              label={t("user:username")}
              helperText={t("user:onboarding.welcomeAboard.username.helperText")}
              placeholder={t("user:onboarding.welcomeAboard.username.placeholder")}
              maxLength={USERNAME_MAX_LENGTH}
              loading={isCheckingUsername}
              iconEnd={isUsernameAvailable ? IconVariants.CHECK : undefined}
            />
          </FormField>

          <FormField
            as="input"
            name="name"
            showError={stateIsShowFieldErrors}
            validations={[
              {
                rule: isRequired(),
                message: t("user:name.error.required"),
              },
              {
                rule: isNotEmptyString(),
                message: t("user:name.error.required"),
              },
              {
                rule: isMinLength(NAME_MIN_LENGTH),
                message: t("user:name.error.tooShort", { minLength: NAME_MIN_LENGTH }),
              },
            ]}
          >
            <Input
              label={t("user:onboarding.welcomeAboard.displayName.label")}
              helperText={t("user:onboarding.welcomeAboard.displayName.helperText")}
              placeholder={t("user:onboarding.welcomeAboard.displayName.placeholder")}
              required={true}
              maxLength={NAME_MAX_LENGTH}
            />
          </FormField>

          <FormField
            name="age"
            showError={stateIsShowFieldErrors}
            validations={[{ rule: isRequired(), message: t("user:onboarding.welcomeAboard.age.error.required") }]}
            as="checkbox"
          >
            <Checkbox label={<Trans i18nKey="user:onboarding.welcomeAboard.age.label" />} size={Size.SMALL} />
          </FormField>

          <FormField
            name="toc"
            showError={stateIsShowFieldErrors}
            validations={[{ rule: isRequired(), message: t("user:onboarding.welcomeAboard.toc.error.required") }]}
            as="checkbox"
          >
            <Checkbox
              label={
                <Trans
                  i18nKey="user:onboarding.welcomeAboard.toc.label"
                  components={{
                    termsLink: <LocalizedLink to="/terms" className="text-blue-9" target="_blank" />,
                    privacyLink: <LocalizedLink to="/privacy" className="text-blue-9" target="_blank" />,
                  }}
                />
              }
              size={Size.SMALL}
            />
          </FormField>
        </form>
      </Formiz>
    </Modal>
  );
}

export default WelcomeAboardModal;
